import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { useDeleteConfigurationMutation } from './__generated__/DeleteConfigurationModal.graphql';

interface Props {
  configurationId: string;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
  isConfigurationSetAsDefault?: boolean;
}

export function DeleteConfigurationModal({
  configurationId,
  isOpen,
  onCancel,
  onSuccess,
  isConfigurationSetAsDefault,
}: Props) {
  const toast = useToast();
  const [deleteConfiguration, { loading }] = useDeleteConfigurationMutation({
    refetchQueries: ['CustomerConfigurationsPage'],
    awaitRefetchQueries: true,
  });
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteConfiguration({
        variables: {
          input: {
            id: configurationId,
          },
        },
      });

      toast({
        status: 'success',
        title: t('configurationDeleted'),
        description: t('configurationDeletedDesc'),
      });

      await onSuccess?.();
      navigate(`../configurations/`);
    } catch (err) {
      toast({
        status: 'error',
        title: t('configurationDeleteErr'),
      });
    } finally {
      onCancel();
    }
  }, [deleteConfiguration, configurationId, toast, onSuccess, navigate, onCancel]);

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      {isConfigurationSetAsDefault ? (
        <ModalContent>
          <ModalHeader>{t('deleteConfiguration')}</ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>{t('configurationSetAsDefaultDeleteError')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose} variant="solid" colorScheme="blue">
              {t('ok')}
            </Button>
          </ModalFooter>
        </ModalContent>
      ) : (
        <ModalContent>
          <ModalHeader>{t('deleteConfiguration')}</ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>{t('deleteConfigurationConfirmation')}</Text>
            <Text>{t('deleteConfigurationDisplayUnassignMsg')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose} variant="ghost" colorScheme="blue" isDisabled={loading}>
              {t('cancel')}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              marginLeft="3"
              onClick={handleDelete}
              isDisabled={loading}
            >
              {t('delete')}
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
    </Modal>
  );
}

DeleteConfigurationModal.graphql = {
  mutations: {
    DeleteConfiguraiton: gql`
      mutation DeleteConfiguration($input: DeleteConfigurationsInput!) {
        deleteConfiguration(input: $input)
      }
    `,
  },
};
